import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type IpOrgPayload, type IpOrganization, type IpOrganizationDetail, type MultiUserOrganization } from '../services/api'
import { resolveOrg, slugFor } from '../lib/orgSlug'
import PageHeader from '../nexus/components/PageHeader'
import StatCard from '../nexus/components/StatCard'

// "Last activity" is a scanning column: an org admin reads it to spot seats that
// are not being used. Relative time answers that at a glance, and the exact
// timestamp stays available on hover for anyone who needs to be precise.
function LastActivity({ value }: { value: string | null | undefined }) {
  if (!value) return <span className="text-base-content/40">Never</span>
  const then = new Date(value)
  const mins = Math.floor((Date.now() - then.getTime()) / 60000)
  const label =
    mins < 1 ? 'Just now'
    : mins < 60 ? `${mins}m ago`
    : mins < 60 * 24 ? `${Math.floor(mins / 60)}h ago`
    : mins < 60 * 24 * 30 ? `${Math.floor(mins / 1440)}d ago`
    : then.toLocaleDateString()
  // Dormant seats are the ones worth acting on, so they get visual weight.
  const stale = mins >= 60 * 24 * 30
  return <span className={stale ? 'text-warning' : ''} title={then.toLocaleString()}>{label}</span>
}

export default function MultiUserOrganizations() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { orgSlug } = useParams<{ orgSlug: string }>()
  const [email, setEmail] = useState('')
  const [sharedDomain, setSharedDomain] = useState('')
  const [seatEmail, setSeatEmail] = useState('')
  const [seatSendEmail, setSeatSendEmail] = useState(true)
  const [seatInviteUrl, setSeatInviteUrl] = useState('')
  const [sendEmail, setSendEmail] = useState(true)
  const [inviteUrl, setInviteUrl] = useState('')
  const [notice, setNotice] = useState('')
  const [reconcileReason, setReconcileReason] = useState('')
  const [reconcileConfirmed, setReconcileConfirmed] = useState(false)
  const [tab, setTab] = useState<'roster' | 'admins' | 'capabilities' | 'network' | 'reconcile' | 'products'>('roster')

  const organizations = useQuery({
    queryKey: ['multi-user-organizations'],
    queryFn: api.getMultiUserOrganizations,
  })
  const networkPolicies = useQuery({
    queryKey: ['ip-organizations'],
    queryFn: api.getIpOrganizations,
  })
  // No organization is selected until an administrator picks one, and the pick
  // lives in the URL rather than in component state.
  //
  // This fell back to `organizations.data?.[0]?.id`, so opening the page loaded
  // whichever organization sorted first and every panel below — roster,
  // administrators, network access, reconciliation — described a tenant nobody
  // had asked for. The actions on those panels include revoking seats, removing
  // administrators and running a repair, so landing pre-focused on an arbitrary
  // customer is an invitation to act on the wrong one. A URL with no slug in it
  // selects nothing, for the same reason.
  //
  // A slug that matches no organization also selects nothing. Falling back to
  // "first in the list" on a mistyped or stale link would reintroduce exactly
  // the bug above, and quietly: the URL would say one tenant and the panels
  // would show another.
  const orgList = organizations.data ?? []
  const selected = useMemo(() => resolveOrg(orgSlug, orgList), [orgSlug, orgList])
  const resolvedId = selected?.id ?? ''
  const unknownSlug = Boolean(orgSlug) && !selected && !organizations.isLoading
  const selectOrg = (org: MultiUserOrganization) => {
    setNotice('')
    setSeatInviteUrl('')
    setInviteUrl('')
    navigate(`/organizations/${slugFor(org, orgList)}`)
  }
  const networkPolicy = networkPolicies.data?.find((policy) => policy.org_account_id === resolvedId)
  const detail = useQuery({
    queryKey: ['multi-user-organization', resolvedId],
    queryFn: () => api.getMultiUserOrganization(resolvedId),
    enabled: Boolean(resolvedId),
  })
  const reconciliation = useQuery({
    queryKey: ['multi-user-reconciliation', resolvedId],
    queryFn: () => api.getOrganizationReconciliation(resolvedId),
    enabled: Boolean(resolvedId),
  })
  const networkDetail = useQuery({
    queryKey: ['ip-organization', networkPolicy?.id],
    queryFn: () => api.getIpOrganization(networkPolicy!.id),
    enabled: Boolean(networkPolicy?.id),
  })

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['multi-user-organizations'] }),
      queryClient.invalidateQueries({ queryKey: ['multi-user-organization', resolvedId] }),
      queryClient.invalidateQueries({ queryKey: ['ip-organizations'] }),
      queryClient.invalidateQueries({ queryKey: ['ip-organization'] }),
    ])
  }

  const inviteMutation = useMutation({
    mutationFn: () => api.createMultiUserAdminInvite(resolvedId, email.trim(), sendEmail),
    onSuccess: async (result) => {
      setInviteUrl(result.url)
      setNotice(result.email_sent ? 'Invitation emailed. The link is also available below.' : `Invitation created. ${result.email_error || 'Copy the link below.'}`)
      setEmail('')
      await refresh()
    },
  })

  const removeAdmin = useMutation({
    mutationFn: ({ adminId, allowFinal }: { adminId: string; allowFinal: boolean }) =>
      api.removeMultiUserAdmin(resolvedId, adminId, allowFinal),
    onSuccess: refresh,
  })

  const revokeInvite = useMutation({
    mutationFn: (inviteId: string) => api.revokeMultiUserAdminInvite(resolvedId, inviteId),
    onSuccess: refresh,
  })

  const retrySync = useMutation({
    mutationFn: (jobId: string) => api.retryMultiUserRelationshipSync(jobId),
    onSuccess: refresh,
  })
  const createSeatInvite = useMutation({
    mutationFn: () => api.createMultiUserSeatInvite(resolvedId, seatEmail.trim(), seatSendEmail),
    onSuccess: async (result) => {
      setSeatInviteUrl(result.url)
      setSeatEmail('')
      setNotice(result.email_sent ? 'Seat invitation emailed. The link is also available below.' : `Seat invitation created. ${result.email_error || 'Copy the link below.'}`)
      // Without this the invitation exists but nothing on screen shows it until
      // the page is reloaded — the link above is the only copy, and navigating
      // away loses it.
      await refresh()
    },
    onError: (error) => setNotice((error as Error).message),
  })
  const createSharedInvite = useMutation({
    mutationFn: () => api.createSharedMemberInvite(resolvedId, sharedDomain),
    onSuccess: async () => {
      setNotice('Shared member link created. Copy it below and send it to the organization administrator to distribute.')
      await refresh()
    },
    onError: (error) => setNotice((error as Error).message),
  })
  const revokeSharedInvite = useMutation({
    mutationFn: (inviteId: string) => api.revokeSharedMemberInvite(resolvedId, inviteId),
    onSuccess: async () => { setNotice('Shared member link revoked. Existing memberships are unchanged.'); await refresh() },
    onError: (error) => setNotice((error as Error).message),
  })
  const revokeSeat = useMutation({
    mutationFn: (seatId: string) => api.revokeMultiUserSeat(resolvedId, seatId),
    onSuccess: async () => { setNotice('Roster member removed.'); await refresh() },
    onError: (error) => setNotice((error as Error).message),
  })
  const reorderQueue = useMutation({
    mutationFn: ({ memberIds, version }: { memberIds: string[]; version: number }) => api.reorderMultiUserQueue(resolvedId, memberIds, version),
    onSuccess: refresh,
    onError: async (error) => { setNotice((error as Error).message); await refresh() },
  })
  const reconcile = useMutation({
    mutationFn: () => api.reconcileOrganization(resolvedId, reconciliation.data!, reconcileReason.trim()),
    onSuccess: async (result) => {
      setNotice(result.result === 'partial' ? 'Reconciliation completed partially; provider work remains retryable.' : 'Reconciliation completed.')
      setReconcileConfirmed(false)
      setReconcileReason('')
      await Promise.all([refresh(), reconciliation.refetch()])
    },
    onError: async (error) => {
      setNotice((error as Error).message.includes('changed') ? 'The report changed. Review the refreshed dry run and confirm again.' : (error as Error).message)
      setReconcileConfirmed(false)
      await reconciliation.refetch()
    },
  })

  if (organizations.isLoading) return <div className="p-6">Loading organizations…</div>
  if (organizations.error) return <div className="p-6 text-error">Failed to load organizations.</div>

  return (
    <div className="p-6">
      <PageHeader
        title="Organizations"
        subtitle="Purchased capacity, members, administrators, network access, and Re:Members synchronization."
      />


      {!organizations.data?.length ? (
        <div className="card bg-base-100 border border-base-300 p-8 text-center text-base-content/60">No qualifying Re:Members organizations have been provisioned.</div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(240px,320px)_minmax(0,1fr)] gap-5 items-start">
          <aside className="card bg-base-100 border border-base-300 p-3 space-y-2">
            {organizations.data.map((org) => <OrgButton key={org.id} org={org} active={org.id === resolvedId} onClick={() => selectOrg(org)} />)}
          </aside>

          <section className="space-y-4">
            {!resolvedId ? (
              <div className="card bg-base-100 border border-base-300 p-8 text-center text-base-content/60">
                {unknownSlug
                  ? <>No organization matches <span className="font-mono">{decodeURIComponent(orgSlug ?? '')}</span>. It may have been renamed — pick it from the list.</>
                  : 'Select an organization to view its roster, administrators and synchronization.'}
              </div>
            ) : detail.isLoading || !detail.data ? <div className="card bg-base-100 border border-base-300 p-6">Loading organization…</div> : (() => {
              const activeAdmins = detail.data.admin_invites.filter((invite) => invite.status === 'active')
              const hasDrift = reconciliation.data ? !reconciliation.data.clean : false
              const hasSyncFailures = detail.data.sync_failures.length > 0
              const networkState = networkPolicy?.is_active ? 'active' : networkPolicy ? 'suspended' : 'off'
              const tabs: { id: typeof tab; label: string; badge?: number; dot?: 'warning' | 'error' | 'success' }[] = [
                { id: 'roster', label: 'Roster', badge: detail.data.roster.members.length },
                { id: 'admins', label: 'Administrators', badge: detail.data.admins.length + activeAdmins.length },
                { id: 'capabilities', label: 'Features' },
                { id: 'network', label: 'Network access', dot: networkState === 'active' ? 'success' : networkState === 'suspended' ? 'warning' : undefined },
                { id: 'reconcile', label: 'Reconciliation', dot: hasDrift ? 'warning' : undefined },
                { id: 'products', label: 'Products', dot: hasSyncFailures ? 'error' : undefined },
              ]
              const dotClass = (d?: string) => d === 'error' ? 'bg-error' : d === 'success' ? 'bg-success' : 'bg-warning'
              return <>
                {/* Summary — always visible above the tabs */}
                <div className="card bg-base-100 border border-base-300">
                  <div className="card-body">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <h2 className="text-xl font-semibold truncate">{selected?.name || selected?.remembers_customer_id}</h2>
                        <p className="text-xs text-base-content/50 mt-1 font-mono">{selected?.remembers_record_number || selected?.remembers_customer_id}</p>
                      </div>
                      <span className={`badge badge-soft ${selected?.provisioning_status === 'active' ? 'badge-success' : 'badge-warning'}`}>{selected?.provisioning_status === 'pending_admin' ? 'Awaiting first admin' : selected?.provisioning_status}</span>
                    </div>
                    {notice && <div className="alert alert-info alert-soft mt-4 py-2 text-sm">{notice}</div>}
                  </div>
                </div>

                {/* Seat capacity */}
                <div className="grid gap-4 sm:grid-cols-3">
                  <StatCard title="Purchased seats" value={detail.data.seat_limit} icon="lucide--package" tone="primary" />
                  <StatCard title="Seats used" value={detail.data.seats_used} icon="lucide--user-check" tone="info" />
                  <StatCard title="Available" value={detail.data.seats_available} icon="lucide--circle-check" tone="success" />
                </div>

                {/* Tabs — real pills (DaisyUI v5 dropped tabs-boxed styling) */}
                <div role="tablist" className="flex flex-wrap gap-1 p-1 rounded-xl bg-base-200 border border-base-300">
                  {tabs.map((t) => {
                    const active = tab === t.id
                    return (
                      <button
                        key={t.id}
                        role="tab"
                        type="button"
                        aria-selected={active}
                        onClick={() => setTab(t.id)}
                        className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                          active
                            ? 'bg-base-100 text-base-content shadow-sm ring-1 ring-base-300'
                            : 'text-base-content/60 hover:text-base-content hover:bg-base-100/60'
                        }`}
                      >
                        {t.label}
                        {typeof t.badge === 'number' && (
                          <span className={`inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full text-xs font-semibold ${active ? 'bg-base-300 text-base-content' : 'bg-base-300/60 text-base-content/60'}`}>{t.badge}</span>
                        )}
                        {t.dot && <span className={`inline-block w-2 h-2 rounded-full ${dotClass(t.dot)}`} />}
                      </button>
                    )
                  })}
                </div>

                {/* Roster */}
                {tab === 'roster' && <div className="card bg-base-100 border border-base-300 p-5">
                  <h3 className="font-semibold">Organization roster</h3>
                  <p className="text-sm text-base-content/60 mt-1">ASM administrators can invite members, remove members, and control queued promotion order. Creating an invitation does not consume a seat.</p>
                  <div className="alert alert-info mt-3 text-sm">
                    <span className="iconify lucide--info size-4 shrink-0" />
                    <span>
                      <strong>Queued members do not receive access through this organization.</strong> A seat is only granted when one becomes
                      available and the member is promoted. Until then they can still use Mat-E on the free tier — the queue delays their
                      organization seat, it does not lock them out of Mat-E.
                    </span>
                  </div>
                  {detail.data.seats_available <= 0 && <div className="alert alert-warning mt-2 text-sm">
                    <span className="iconify lucide--triangle-alert size-4 shrink-0" />
                    <span>
                      All {detail.data.seat_limit} seat{detail.data.seat_limit === 1 ? '' : 's'} are in use, so anyone invited now joins the queue rather than
                      receiving a seat. They are still emailed the invitation — consider telling them about the wait yourself, as the email does not.
                    </span>
                  </div>}
                  <section className="rounded-lg border border-base-300 p-4 mt-4" aria-label="Shared member links">
                    <h4 className="font-semibold">Shared member links</h4>
                    <p className="text-sm text-base-content/60 mt-1">Create a reusable link for the organization administrator to distribute. Anyone with the link can sign in and join this organization, subject to the optional email-domain restriction. It does not grant admin permissions.</p>
                    <p className="text-sm text-base-content/60 mt-1">Reusable until an ASM administrator revokes it. New members receive available seats or join the queue.</p>
                    <label className="block text-sm mt-3">Email domain (optional)<input className="input input-bordered block mt-1 w-full max-w-sm" placeholder="company.com" value={sharedDomain} onChange={(event) => setSharedDomain(event.target.value)} /></label>
                    <p className="text-xs text-base-content/60 mt-1">Leave blank to allow any signed-in email. Only the exact domain matches; subdomains are not included.</p>
                    <button type="button" className="btn btn-primary mt-3" disabled={createSharedInvite.isPending} onClick={() => createSharedInvite.mutate()}>{createSharedInvite.isPending ? 'Creating…' : 'Generate shared link'}</button>
                    {(detail.data.shared_invites ?? []).map((invite) => <div key={invite.id} className="mt-3 space-y-2">
                      <p className="text-sm">{invite.domain_lock ? `Only @${invite.domain_lock}` : 'Any email domain'}</p>
                      <p className="text-xs text-base-content/60">{invite.used_count} uses{invite.max_uses == null ? ' · Reusable until revoked' : ` of ${invite.max_uses}`}{invite.expires_at ? ` · expires ${new Date(invite.expires_at).toLocaleDateString()}` : ''}</p>
                      <div className="flex gap-2"><input className="input input-bordered flex-1 font-mono text-xs min-w-0" aria-label="Shared member invitation link" readOnly value={invite.url} /><button type="button" className="btn btn-sm" onClick={() => navigator.clipboard.writeText(invite.url)}>Copy link</button><button type="button" className="btn btn-ghost btn-sm" disabled={revokeSharedInvite.isPending} onClick={() => revokeSharedInvite.mutate(invite.id)}>Revoke</button></div>
                    </div>)}
                  </section>
                  <form className="flex gap-2 mt-4" onSubmit={(event) => { event.preventDefault(); createSeatInvite.mutate() }}>
                    <input className="input input-bordered flex-1" type="email" required value={seatEmail} onChange={(event) => setSeatEmail(event.target.value)} placeholder="member@example.com" />
                    <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap px-1"><input className="checkbox checkbox-sm" type="checkbox" checked={seatSendEmail} onChange={(event) => setSeatSendEmail(event.target.checked)} /><span className="text-sm">Email</span></label>
                    <button className="btn btn-primary" disabled={createSeatInvite.isPending || !seatEmail.trim()}>{createSeatInvite.isPending ? 'Creating…' : 'Create invite'}</button>
                  </form>
                  {createSeatInvite.error && <p className="text-error text-sm mt-2">{(createSeatInvite.error as Error).message}</p>}
                  {seatInviteUrl && <div className="join w-full mt-2"><input className="input input-bordered join-item flex-1 font-mono text-xs" aria-label="Seat invitation link" readOnly value={seatInviteUrl} /><button type="button" className="btn join-item" onClick={() => navigator.clipboard.writeText(seatInviteUrl)}>Copy</button></div>}
                  {(detail.data.seat_invites ?? []).length > 0 && <div className="mt-4 space-y-2">
                    <h4 className="text-sm font-semibold">Pending invitations</h4>
                    {(detail.data.seat_invites ?? []).map((invite) => <div key={invite.id} className="flex items-center gap-2 rounded-lg border border-base-300 p-2">
                      <div className="min-w-0 flex-1"><div className="truncate text-sm">{invite.email_lock}</div><div className="text-xs text-base-content/50">Not accepted{invite.expires_at ? ` · expires ${new Date(invite.expires_at).toLocaleDateString()}` : ''}</div></div>
                      <button type="button" className="btn btn-sm" onClick={() => navigator.clipboard.writeText(invite.url)}>Copy link</button>
                    </div>)}
                  </div>}
                  <div className="overflow-x-auto mt-4">
                    <table className="table table-sm">
                      <thead><tr><th>Email</th><th>Status</th><th>Source</th><th>Last activity</th><th>Relationship</th><th></th></tr></thead>
                      <tbody>{detail.data.roster.members.map((member) => {
                        const queued = detail.data.roster.members.filter((item) => item.state === 'queued')
                        const queueIndex = queued.findIndex((item) => item.id === member.id)
                        const move = (offset: number) => {
                          const reordered = [...queued]
                          const target = queueIndex + offset
                          if (target < 0 || target >= reordered.length) return
                          ;[reordered[queueIndex], reordered[target]] = [reordered[target], reordered[queueIndex]]
                          reorderQueue.mutate({ memberIds: reordered.map((item) => item.id), version: detail.data.roster.queue_version })
                        }
                        return <tr key={member.id}>
                          <td className="font-medium">{member.email}</td>
                          <td><span className={`badge badge-sm ${member.state === 'active' ? 'badge-success' : 'badge-warning'}`}>{member.state}</span></td>
                          <td>{member.qualification_source}</td>
                          <td className="whitespace-nowrap"><LastActivity value={member.last_activity_at} /></td>
                          <td>{member.relationship_sync_status}</td>
                          <td className="text-right whitespace-nowrap">
                            {member.state === 'queued' && <><button className="btn btn-ghost btn-xs" disabled={queueIndex === 0 || reorderQueue.isPending} onClick={() => move(-1)}>Up</button><button className="btn btn-ghost btn-xs" disabled={queueIndex === queued.length - 1 || reorderQueue.isPending} onClick={() => move(1)}>Down</button></>}
                            <button className="btn btn-ghost btn-xs text-error" disabled={revokeSeat.isPending} onClick={() => revokeSeat.mutate(member.id)}>Remove</button>
                          </td>
                        </tr>
                      })}</tbody>
                    </table>
                    {!detail.data.roster.members.length && <p className="text-sm text-base-content/50 py-4 text-center">No roster members.</p>}
                  </div>
                </div>}

                {/* Administrators */}
                {tab === 'admins' && <div className="card bg-base-100 border border-base-300 p-5">
                  <p className="text-sm text-base-content/60">Admins manage invitations but consume no seat unless they separately accept one.</p>
                  <form className="flex gap-2 mt-4" onSubmit={(event) => { event.preventDefault(); inviteMutation.mutate() }}>
                    <input className="input input-bordered flex-1" type="email" required value={email} onChange={(event) => setEmail(event.target.value)} placeholder="customer-admin@example.com" />
                    <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap px-1"><input className="checkbox checkbox-sm" type="checkbox" checked={sendEmail} onChange={(event) => setSendEmail(event.target.checked)} /><span className="text-sm">Email</span></label>
                    <button className="btn btn-primary" disabled={inviteMutation.isPending || !email.trim()}>{inviteMutation.isPending ? 'Creating…' : 'Invite admin'}</button>
                  </form>
                  {inviteMutation.error && <p className="text-error text-sm mt-2">{(inviteMutation.error as Error).message}</p>}
                  {inviteUrl && <div className="join w-full mt-2"><input className="input input-bordered join-item flex-1 font-mono text-xs" readOnly value={inviteUrl} /><button className="btn join-item" onClick={() => navigator.clipboard.writeText(inviteUrl)}>Copy</button></div>}

                  <div className="divide-y divide-base-300 mt-4">
                    {detail.data.admins.map((admin) => (
                      <div key={admin.id} className="py-3 flex items-center justify-between">
                        <div><div className="font-medium">{admin.email}</div><div className="text-xs text-base-content/50">Accepted {admin.accepted_at ? new Date(admin.accepted_at).toLocaleDateString() : 'before invitation tracking'}</div></div>
                        {/* Removing the last admin is allowed — leaving one in place
                            made a mistyped or departed sole admin permanently stuck,
                            and ASM can invite a replacement from this page. It just
                            has to be deliberate. */}
                        <button
                          className="btn btn-ghost btn-sm text-error"
                          disabled={removeAdmin.isPending}
                          onClick={() => {
                            const isFinal = detail.data.admins.length === 1
                            if (isFinal && !window.confirm(
                              `Remove ${admin.email}, the last administrator of this organization?\n\n` +
                              'It will have no administrator until you invite another. Seats, ' +
                              'members, and access are unaffected.'
                            )) return
                            removeAdmin.mutate({ adminId: admin.id, allowFinal: isFinal })
                          }}
                        >Remove</button>
                      </div>
                    ))}
                    {activeAdmins.map((invite) => (
                      <div key={invite.id} className="py-3 flex items-center justify-between">
                        <div><div className="font-medium">{invite.email}</div><div className="text-xs text-base-content/50">Pending · expires {new Date(invite.expires_at).toLocaleDateString()}</div></div>
                        <button className="btn btn-ghost btn-sm" disabled={revokeInvite.isPending} onClick={() => revokeInvite.mutate(invite.id)}>Revoke</button>
                      </div>
                    ))}
                  </div>
                </div>}

                {/* Network access */}
                {tab === 'capabilities' && <OrganizationCapabilitiesCard key={resolvedId} org={detail.data.org} refresh={refresh} />}

                {/* Network access */}
                {tab === 'network' && <NetworkAccessCard
                  key={resolvedId}
                  organization={selected!}
                  policy={networkPolicy}
                  detail={networkDetail.data}
                  loading={networkPolicies.isLoading || networkDetail.isLoading}
                  refresh={refresh}
                />}

                {/* Reconciliation */}
                {tab === 'reconcile' && <div className="card bg-base-100 border border-base-300 p-5">
                  <h3 className="font-semibold">Reconciliation dry run</h3>
                  <div className="flex justify-between gap-3 mt-1"><p className="text-sm text-base-content/60">Read-only comparison of purchased capacity, memberships, relationships, and entitlement sources.</p><button className="btn btn-sm" onClick={() => reconciliation.refetch()}>Refresh dry run</button></div>
                  {reconciliation.data && <>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-4"><Metric label="Purchased" value={reconciliation.data.capacity.purchased} /><Metric label="Active" value={reconciliation.data.memberships.active} /><Metric label="Queued" value={reconciliation.data.memberships.queued} /><Metric label="Relationships" value={reconciliation.data.relationships.provider_observed_active} /><Metric label="Mismatches" value={reconciliation.data.drift.length} /></div>
                    <p className={`mt-3 text-sm ${reconciliation.data.clean ? 'text-success' : 'text-warning'}`}>{reconciliation.data.clean ? 'No drift detected.' : `Drift: ${reconciliation.data.drift.join(', ')}`}</p>
                    {reconciliation.data.next_queued_member && <p className="text-sm mt-1">Next queued: {reconciliation.data.next_queued_member.email}</p>}
                    {reconciliation.data.provider.status === 'unavailable' && <div className="alert alert-warning mt-3">Re:Members is unavailable. Local repairs can complete partially and provider work remains retryable.</div>}
                    <input className="input input-bordered w-full mt-4" value={reconcileReason} onChange={(event) => setReconcileReason(event.target.value)} placeholder="Required audit reason" />
                    <label className="flex items-center gap-2 cursor-pointer w-fit"><input type="checkbox" className="checkbox checkbox-sm" checked={reconcileConfirmed} onChange={(event) => setReconcileConfirmed(event.target.checked)} /><span className="text-sm">I confirm reconciliation for this organization</span></label>
                    <button className="btn btn-warning" disabled={!reconcileConfirmed || !reconcileReason.trim() || reconcile.isPending} onClick={() => reconcile.mutate()}>{reconcile.isPending ? 'Reconciling…' : 'Apply reconciliation'}</button>
                  </>}
                </div>}

                {/* Products and synchronization */}
                {tab === 'products' && <div className="card bg-base-100 border border-base-300 p-5">
                  <div className="overflow-x-auto"><table className="table table-sm"><thead><tr><th>Product</th><th>Order</th><th>Seats</th><th>Status</th></tr></thead><tbody>{detail.data.subscriptions.map((sub) => <tr key={`${sub.product_code}-${sub.source_order_number}`}><td>{sub.product_code}<div className="text-xs text-base-content/50">{sub.product_name}</div></td><td>{sub.source_order_number}</td><td>{sub.seat_quantity}</td><td>{sub.status}</td></tr>)}</tbody></table></div>
                  {detail.data.sync_failures.length > 0 && <div className="mt-4 space-y-2">{detail.data.sync_failures.map((job) => <div key={job.id} className="alert alert-warning"><div><div className="font-medium">Relationship update failed for {job.assigned_email}</div><div className="text-xs mt-1">{job.last_error || 'Re:Members did not accept the update.'}</div></div><button className="btn btn-sm" disabled={retrySync.isPending} onClick={() => retrySync.mutate(job.id)}>{retrySync.isPending ? 'Retrying…' : 'Retry now'}</button></div>)}</div>}
                </div>}
              </>
            })()}
          </section>
        </div>
      )}
    </div>
  )
}

function OrganizationCapabilitiesCard({ org, refresh }: { org: MultiUserOrganization; refresh: () => Promise<void> }) {
  const [voice, setVoice] = useState(org.allow_realtime_voice !== false)
  const [uploads, setUploads] = useState(org.allow_file_uploads !== false)
  const [translation, setTranslation] = useState(org.allow_translation !== false)
  // `!== false` so an organization predating this column reads as consenting,
  // matching the column default. `=== true` would silently withdraw consent for
  // every existing customer the first time this card was opened.
  const [followup, setFollowup] = useState(org.allow_followup_contact !== false)
  const [extensions, setExtensions] = useState((org.allowed_upload_extensions || []).join(', '))
  const save = useMutation({
    mutationFn: () => api.updateMultiUserOrganizationCapabilities(org.id, {
      realtime_voice: voice,
      file_uploads: uploads,
      translation,
      followup_contact: followup,
      allowed_upload_extensions: extensions.trim() ? extensions.split(',').map((value) => value.trim()).filter(Boolean) : null,
    }),
    onSuccess: refresh,
  })
  return <div className="card bg-base-100 border border-base-300 p-5">
    <h3 className="font-semibold">Member features</h3>
    <p className="text-sm text-base-content/60 mt-1">Controls apply to active members. Platform-wide feature flags remain the upper limit.</p>
    <div className="space-y-3 mt-4">
      {/* Voice is off on legal advice (Sept 2026) — see frontend/src/lib/voice-disabled.ts.
          The org capability is still stored and sent; only the control is hidden, so
          this can be reversed by removing the `false &&`. */}
      {false && <label className="flex items-center justify-between"><span>Realtime voice</span><input className="toggle" type="checkbox" checked={voice} onChange={(event) => setVoice(event.target.checked)} /></label>}
      <label className="flex items-center justify-between"><span>File uploads</span><input className="toggle" type="checkbox" checked={uploads} onChange={(event) => setUploads(event.target.checked)} /></label>
      <label className="flex items-center justify-between"><span>Translation</span><input className="toggle" type="checkbox" checked={translation} onChange={(event) => setTranslation(event.target.checked)} /></label>
      <div className="border-t border-base-300 pt-3">
        <label className="flex items-center justify-between"><span>Allow ASM to follow up on responses</span><input className="toggle" type="checkbox" checked={followup} onChange={(event) => setFollowup(event.target.checked)} /></label>
        {/* Says what it does, because this one is not a feature the
            organization is buying — it is a consent it can withhold on its
            members' behalf, and it cannot grant one a member has refused. */}
        <p className="text-xs text-base-content/50 mt-1">Turning this off withholds follow-up for every member of this organization, whatever they have chosen individually. Leaving it on lets each member decide for themselves.</p>
      </div>
      <label className="form-control"><span className="label-text mb-1">Allowed upload extensions</span><input className="input input-bordered" value={extensions} disabled={!uploads} onChange={(event) => setExtensions(event.target.value)} placeholder="All supported types (or .pdf, .csv, .txt)" /><span className="text-xs text-base-content/50 mt-1">Comma-separated; blank allows all supported types.</span></label>
    </div>
    <div className="mt-4"><button className="btn btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>{save.isPending ? 'Saving…' : 'Save features'}</button></div>
    {save.error && <p className="text-error text-sm mt-2">{(save.error as Error).message}</p>}
  </div>
}

function OrgButton({ org, active, onClick }: { org: MultiUserOrganization; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left rounded-box border p-3 transition-colors ${active ? 'border-primary/50 bg-primary/10' : 'border-base-300 hover:bg-base-200'}`}
    >
      <div className="font-semibold text-sm truncate">{org.name || org.remembers_customer_id}</div>
      <div className="text-xs text-base-content/50 mt-1">
        {org.seats_used}/{org.seat_limit} seats · {org.admin_count} admin{org.admin_count === 1 ? '' : 's'}
      </div>
      {/* Queued members and unredeemed invitations are both invisible in
          seats_used — one is status='revoked', the other has no seat row at
          all — so an org with people waiting looked identical to a quiet one.
          Shown only when non-zero: a row of zeroes is noise on every org. */}
      {(org.queued_count > 0 || org.pending_invite_count > 0) && <div className="flex flex-wrap gap-1 mt-1.5">
        {org.queued_count > 0 && <span className="badge badge-soft badge-warning badge-sm" title={`${org.queued_count} member${org.queued_count === 1 ? '' : 's'} waiting for a seat`}>{org.queued_count} queued</span>}
        {org.pending_invite_count > 0 && <span className="badge badge-soft badge-info badge-sm" title={`${org.pending_invite_count} invitation${org.pending_invite_count === 1 ? '' : 's'} not yet accepted`}>{org.pending_invite_count} pending</span>}
      </div>}
      {org.provisioning_status === 'pending_admin' && <span className="badge badge-soft badge-warning badge-sm mt-2">Needs admin</span>}
    </button>
  )
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-box border border-base-300 bg-base-200/40 px-3.5 py-3">
      <div className="text-xl font-semibold font-mono">{value}</div>
      <div className="text-xs text-base-content/60 mt-0.5">{label}</div>
    </div>
  )
}

function NetworkAccessCard({
  organization,
  policy,
  detail,
  loading,
  refresh,
}: {
  organization: MultiUserOrganization
  policy?: IpOrganization
  detail?: IpOrganizationDetail
  loading: boolean
  refresh: () => Promise<void>
}) {
  const [editing, setEditing] = useState(false)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const run = async (action: () => Promise<unknown>) => {
    setBusy(true)
    setError('')
    try {
      await action()
      await refresh()
      return true
    } catch (value) {
      setError((value as Error).message)
      return false
    } finally {
      setBusy(false)
    }
  }

  if (loading) return <div className="card bg-base-100 border border-base-300 p-5">Loading network access…</div>

  return <div className="card bg-base-100 border border-base-300 p-5">
    <div className="flex items-start justify-between gap-4">
      <div>
        <h3 className="font-semibold">Network access</h3>
        <p className="text-sm text-base-content/60 mt-1">Optional approved-network login for this organization. It uses the same purchased capacity and roster.</p>
      </div>
      <span className={`badge ${policy?.is_active ? 'badge-success' : policy ? 'badge-warning' : 'badge-ghost'}`}>
        {policy?.is_active ? 'Active' : policy ? 'Suspended' : 'Not configured'}
      </span>
    </div>

    {editing ? <NetworkAccessEditor
      organization={organization}
      policy={policy}
      busy={busy}
      error={error}
      onCancel={() => { setEditing(false); setError('') }}
      onSave={async (payload) => {
        const saved = await run(() => policy ? api.updateIpOrganization(policy.id, payload) : api.createIpOrganization(payload))
        if (saved) setEditing(false)
      }}
    /> : !policy ? <div className="mt-4">
      <p className="text-sm text-base-content/70">No CIDRs or access link have been configured. The purchased organization and roster remain available.</p>
      <button className="btn btn-primary btn-sm mt-3" onClick={() => setEditing(true)}>Configure network access</button>
    </div> : <div className="space-y-4 mt-4">
      <div className="flex flex-wrap gap-2">
        {policy.ip_entries.map((entry) => <span key={entry} className="badge badge-ghost font-mono">{entry}</span>)}
        {!policy.ip_entries.length && <span className="text-sm text-warning">No approved networks configured.</span>}
      </div>
      <div>
        <div className="text-sm font-medium mb-1">Organization access link</div>
        <div className="join w-full">
          <input className="input input-bordered input-sm join-item flex-1 font-mono text-xs" readOnly value={policy.access_url} />
          <button className="btn btn-sm join-item" onClick={() => navigator.clipboard.writeText(policy.access_url)}>Copy</button>
          <button className="btn btn-sm join-item" disabled={busy} onClick={() => run(() => api.rotateIpOrganizationLink(policy.id))}>Rotate</button>
        </div>
        <p className="text-xs text-base-content/50 mt-1">The link starts sign-in but never bypasses approved-network validation.</p>
      </div>
      {detail?.events.length ? <details className="border-t border-base-300 pt-3">
        <summary className="text-sm font-medium cursor-pointer">Recent network-access events</summary>
        <div className="max-h-48 overflow-y-auto mt-2 text-sm">
          {detail.events.map((event) => <div key={event.id} className="grid grid-cols-3 gap-2 border-b border-base-300 py-1">
            <span>{event.event_type}</span>
            <span>{event.email || event.reason_code || '—'}</span>
            <span>{new Date(event.created_at).toLocaleString()}</span>
          </div>)}
        </div>
      </details> : null}
      <div className="flex justify-between gap-3 pt-2 border-t border-base-300">
        <button className="btn btn-ghost btn-sm text-error" disabled={busy} onClick={() => {
          if (window.confirm('Remove network access from this organization? Its purchase, roster, and administrators will remain.')) {
            run(() => api.deleteIpOrganization(policy.id))
          }
        }}>Remove network access</button>
        <button className="btn btn-sm" onClick={() => setEditing(true)}>Edit network access</button>
      </div>
      {error && <p className="text-error text-sm">{error}</p>}
    </div>}
  </div>
}

function NetworkAccessEditor({
  organization,
  policy,
  busy,
  error,
  onCancel,
  onSave,
}: {
  organization: MultiUserOrganization
  policy?: IpOrganization
  busy: boolean
  error: string
  onCancel: () => void
  onSave: (payload: IpOrgPayload) => Promise<void>
}) {
  const [cidrs, setCidrs] = useState((policy?.ip_entries || []).join('\n'))
  const [active, setActive] = useState(policy?.is_active ?? true)
  const lines = (value: string) => value.split('\n').map((line) => line.trim()).filter(Boolean)

  return <form className="space-y-3 mt-4" onSubmit={(event) => {
    event.preventDefault()
    onSave({
      name: organization.name || organization.remembers_customer_id,
      org_account_id: organization.id,
      ip_entries: lines(cidrs),
      is_active: active,
    })
  }}>
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium">Approved IP addresses or CIDR ranges</label>
      <textarea className="textarea textarea-bordered w-full h-32 font-mono text-sm leading-relaxed" required value={cidrs} onChange={(event) => setCidrs(event.target.value)} placeholder={'203.0.113.0/24\n198.51.100.10'} />
      <span className="text-xs text-base-content/50">One public IP address or CIDR range per line.</span>
    </div>
    <label className="flex items-center gap-2 cursor-pointer w-fit">
      <input type="checkbox" className="checkbox checkbox-sm" checked={active} onChange={(event) => setActive(event.target.checked)} />
      <span className="text-sm">Network access active</span>
    </label>
    {error && <p className="text-error text-sm">{error}</p>}
    <div className="flex justify-end gap-2">
      <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={onCancel}>Cancel</button>
      <button className="btn btn-primary btn-sm" disabled={busy || !lines(cidrs).length}>{busy ? 'Saving…' : 'Save network access'}</button>
    </div>
  </form>
}
